/**
 * Wishlist Management Page
 * Handles viewing, editing, and submitting wishlists
 */

class WishlistPage {
  constructor() {
    this.api = null;
    this.auth = null;
    this.wishlists = [];
    this.currentWishlist = null;
    this.viewMode = 'index'; // 'index' or 'detail'
    this.waitForDependencies();
  }

  async waitForDependencies() {
    // Wait for WishlistAPI and WishlistAuth to load
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max
    
    const checkInterval = setInterval(() => {
      attempts++;
      
      if (window.WishlistAPI && window.WishlistAuth) {
        clearInterval(checkInterval);
        this.api = window.WishlistAPI;
        this.auth = window.WishlistAuth;
        this.init();
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        console.error('WishlistAPI or WishlistAuth failed to load');
        this.showError('Failed to load wishlist system. Please refresh the page.');
      }
    }, 100);
  }

  async init() {
    // Safety check - ensure auth is loaded
    if (!this.auth) {
      console.error('WishlistAuth not available in init()');
      this.showError('Authentication system not loaded. Please refresh the page.');
      return;
    }

    // Check authentication
    if (!this.auth.checkAuth || typeof this.auth.checkAuth !== 'function') {
      console.error('WishlistAuth.checkAuth is not a function');
      this.showError('Authentication system error. Please refresh the page.');
      return;
    }

    if (!this.auth.checkAuth()) {
      this.showAuthRequired();
      return;
    }

    // Check URL for detail view
    const urlParams = new URLSearchParams(window.location.search);
    const wishlistId = urlParams.get('id');
    if (wishlistId) {
      this.viewMode = 'detail';
      this.showWishlistDetail(wishlistId);
      return;
    }

    // Show create button and header since user is authenticated
    this.updateCreateButtonVisibility(true);
    const header = document.querySelector('[data-wishlist-header]');
    if (header) {
      header.style.display = '';
    }

    // Remove logged-out class
    const page = document.querySelector('.wishlist-page');
    if (page) {
      page.classList.remove('is-logged-out');
    }

    // Set up event listeners
    this.setupEventListeners();

    // Handle browser back/forward navigation
    window.addEventListener('popstate', () => {
      const urlParams = new URLSearchParams(window.location.search);
      const wishlistId = urlParams.get('id');
      if (wishlistId) {
        this.viewMode = 'detail';
        this.showWishlistDetail(wishlistId);
      } else {
        this.viewMode = 'index';
        const detailContainer = document.querySelector('[data-wishlist-detail]');
        const content = document.querySelector('[data-wishlist-content]');
        if (detailContainer) detailContainer.style.display = 'none';
        if (content) content.style.display = 'block';
        this.loadWishlists();
      }
    });

    // Listen for items added from product pages
    document.addEventListener('wishlist:item:added', async (e) => {
      const { wishlistId } = e.detail;
      // Check if this wishlist was submitted and reset to draft
      await this.checkAndResetSubmittedStatus(wishlistId);
      // Reload wishlists to show updated status
      if (this.viewMode === 'detail' && this.currentWishlist && this.currentWishlist.id === wishlistId) {
        await this.showWishlistDetail(wishlistId);
      }
      await this.loadWishlists();
    });

    // Load wishlists
    await this.loadWishlists();
  }

  setupEventListeners() {
    // Create wishlist button
    const createBtn = document.querySelector('[data-create-wishlist]');
    if (createBtn) {
      createBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.api) {
          this.showCreateModal();
        } else {
          this.showError('Wishlist system is still loading. Please wait...');
        }
      });
    }

    // Back button (for detail view)
    const backBtn = document.querySelector('[data-back-button]');
    if (backBtn) {
      backBtn.addEventListener('click', (e) => {
        e.preventDefault();
        window.history.pushState({}, '', '/pages/wishlist');
        this.viewMode = 'index';
        this.loadWishlists();
      });
    }
  }

  async loadWishlists() {
    if (!this.api) {
      console.error('WishlistAPI not available');
      return;
    }

    const content = document.querySelector('[data-wishlist-content]');
    if (!content) return;

    const loading = content.querySelector('[data-loading]');
    const empty = content.querySelector('[data-empty]');
    const wishlistsContainer = content.querySelector('[data-wishlists]');

    try {
      if (loading) loading.style.display = 'block';
      if (empty) empty.style.display = 'none';
      if (wishlistsContainer) wishlistsContainer.style.display = 'none';

      // Load all wishlists (not filtered by tab)
      const draftResponse = await this.api.getWishlists('draft', true);
      const submittedResponse = await this.api.getWishlists('submitted', true);
      const archivedResponse = await this.api.getWishlists('archived', true);
      
      // Combine all wishlists
      let allWishlists = [
        ...(draftResponse.wishlists || []),
        ...(submittedResponse.wishlists || []),
        ...(archivedResponse.wishlists || [])
      ];
      
      // For submitted wishlists, always fetch full details to get submission data
      // (Date and venue are collected during submission, so they're in the submission record)
      this.wishlists = await Promise.all(allWishlists.map(async (wishlist) => {
        // If wishlist is submitted, fetch full details to get submission data
        if (wishlist.status === 'submitted' || wishlist.submitted_at) {
          try {
            const fullWishlistResponse = await this.api.getWishlist(wishlist.id);
            console.log(`🔍 RAW API RESPONSE for wishlist ${wishlist.id}:`, fullWishlistResponse);
            console.log(`   Response type:`, typeof fullWishlistResponse);
            console.log(`   Response keys:`, Object.keys(fullWishlistResponse));
            
            // API might return { wishlist: {...} } or just the wishlist object
            const fullWishlist = fullWishlistResponse.wishlist || fullWishlistResponse;
            console.log(`   Full wishlist keys:`, Object.keys(fullWishlist));
            console.log(`   Full wishlist JSON:`, JSON.stringify(fullWishlist, null, 2));
            
            // Try fetching submission data separately if it exists
            let submissionData = null;
            try {
              submissionData = await this.api.getWishlistSubmission(wishlist.id);
              console.log(`   📋 Separate submission data:`, submissionData);
            } catch (error) {
              console.log(`   No separate submission endpoint (this is OK):`, error.message);
            }
            
            // Check if submission data is at root level (common API pattern)
            // The API might return submission fields directly on the wishlist object
            const rootLevelSubmissionData = {
              loadInDate: fullWishlist.loadInDate,
              load_in_date: fullWishlist.load_in_date,
              loadOutDate: fullWishlist.loadOutDate,
              load_out_date: fullWishlist.load_out_date,
              loadInTime: fullWishlist.loadInTime,
              load_in_time: fullWishlist.load_in_time,
              loadOutTime: fullWishlist.loadOutTime,
              load_out_time: fullWishlist.load_out_time,
              venueName: fullWishlist.venueName,
              venue_name: fullWishlist.venue_name,
              venueAddress: fullWishlist.venueAddress,
              venue_address: fullWishlist.venue_address
            };
            
            console.log(`   Root-level submission data:`, rootLevelSubmissionData);
            
            // Extract submission data from nested object OR root level
            const submission = fullWishlist.submission || fullWishlist.submission_data || fullWishlist.submissionData || {};
            console.log(`   Submission object:`, submission);
            console.log(`   Submission type:`, typeof submission);
            console.log(`   Submission keys:`, submission && typeof submission === 'object' ? Object.keys(submission) : 'not an object');
            
            // Extract submission fields - check root level FIRST, then nested submission object
            const extractedData = {};
            
            // Backend returns snake_case fields (load_in_date, venue_name, etc.)
            // Check root level first (backend stores these directly on wishlist)
            if (fullWishlist.load_in_date) {
              extractedData.loadInDate = fullWishlist.load_in_date;
              extractedData.load_in_date = fullWishlist.load_in_date;
            }
            if (fullWishlist.load_out_date) {
              extractedData.loadOutDate = fullWishlist.load_out_date;
              extractedData.load_out_date = fullWishlist.load_out_date;
            }
            if (fullWishlist.load_in_time) {
              extractedData.loadInTime = fullWishlist.load_in_time;
              extractedData.load_in_time = fullWishlist.load_in_time;
            }
            if (fullWishlist.load_out_time) {
              extractedData.loadOutTime = fullWishlist.load_out_time;
              extractedData.load_out_time = fullWishlist.load_out_time;
            }
            if (fullWishlist.venue_name) {
              extractedData.venueName = fullWishlist.venue_name;
              extractedData.venue_name = fullWishlist.venue_name;
            }
            if (fullWishlist.venue_address) {
              extractedData.venueAddress = fullWishlist.venue_address;
              extractedData.venue_address = fullWishlist.venue_address;
            }
            
            // Also check camelCase variants (for backwards compatibility)
            if (!extractedData.loadInDate && fullWishlist.loadInDate) {
              extractedData.loadInDate = fullWishlist.loadInDate;
              extractedData.load_in_date = fullWishlist.loadInDate;
            }
            if (!extractedData.loadOutDate && fullWishlist.loadOutDate) {
              extractedData.loadOutDate = fullWishlist.loadOutDate;
              extractedData.load_out_date = fullWishlist.loadOutDate;
            }
            if (!extractedData.loadInTime && fullWishlist.loadInTime) {
              extractedData.loadInTime = fullWishlist.loadInTime;
              extractedData.load_in_time = fullWishlist.loadInTime;
            }
            if (!extractedData.loadOutTime && fullWishlist.loadOutTime) {
              extractedData.loadOutTime = fullWishlist.loadOutTime;
              extractedData.load_out_time = fullWishlist.loadOutTime;
            }
            if (!extractedData.venueName && fullWishlist.venueName) {
              extractedData.venueName = fullWishlist.venueName;
              extractedData.venue_name = fullWishlist.venueName;
            }
            if (!extractedData.venueAddress && fullWishlist.venueAddress) {
              extractedData.venueAddress = fullWishlist.venueAddress;
              extractedData.venue_address = fullWishlist.venueAddress;
            }
            
            // Then check nested submission object if root level didn't have data
            if (submission && typeof submission === 'object' && Object.keys(submission).length > 0) {
              for (const key in submission) {
                const lowerKey = key.toLowerCase();
                // Map submission fields to common names
                if (lowerKey.includes('load') && lowerKey.includes('in') && !lowerKey.includes('out')) {
                  if (lowerKey.includes('time')) {
                    extractedData.load_in_time = extractedData.load_in_time || submission[key];
                    extractedData.loadInTime = extractedData.loadInTime || submission[key];
                  } else {
                    extractedData.load_in_date = extractedData.load_in_date || submission[key];
                    extractedData.loadInDate = extractedData.loadInDate || submission[key];
                  }
                }
                if (lowerKey.includes('load') && lowerKey.includes('out')) {
                  if (lowerKey.includes('time')) {
                    extractedData.load_out_time = extractedData.load_out_time || submission[key];
                    extractedData.loadOutTime = extractedData.loadOutTime || submission[key];
                  } else {
                    extractedData.load_out_date = extractedData.load_out_date || submission[key];
                    extractedData.loadOutDate = extractedData.loadOutDate || submission[key];
                  }
                }
                if (lowerKey.includes('venue') && !lowerKey.includes('address')) {
                  extractedData.venue_name = extractedData.venue_name || submission[key];
                  extractedData.venueName = extractedData.venueName || submission[key];
                }
                if (lowerKey.includes('venue') && lowerKey.includes('address')) {
                  extractedData.venue_address = extractedData.venue_address || submission[key];
                  extractedData.venueAddress = extractedData.venueAddress || submission[key];
                }
              }
            }
            
            console.log(`   Extracted submission data:`, extractedData);
            
            // Merge: wishlist data, full wishlist data, and extracted submission data
            const merged = { 
              ...wishlist, 
              ...fullWishlist,
              ...extractedData,
              submission // Keep submission object too
            };
            
            console.log(`📥 Fetched full wishlist ${wishlist.id}`);
            console.log(`   Extracted submission data:`, extractedData);
            console.log(`   Merged loadInDate:`, merged.loadInDate || merged.load_in_date);
            console.log(`   Merged venueName:`, merged.venueName || merged.venue_name);
            
            return merged;
          } catch (error) {
            console.warn('Failed to fetch full wishlist details:', error);
            return wishlist;
          }
        }
        return wishlist;
      }));
      
      // Debug: Log all wishlists to see available fields
      console.log('=== WISHLIST DATA DEBUG ===');
      this.wishlists.forEach((w, idx) => {
        console.log(`Wishlist ${idx + 1} (${w.name}, status: ${w.status}):`);
        console.log(`  All keys:`, Object.keys(w));
        console.log(`  Full object:`, JSON.stringify(w, null, 2));
        if (w.submission) {
          console.log(`  Submission keys:`, Object.keys(w.submission));
          console.log(`  Submission:`, JSON.stringify(w.submission, null, 2));
        }
        if (w.submission_data) {
          console.log(`  Submission_data keys:`, Object.keys(w.submission_data));
          console.log(`  Submission_data:`, JSON.stringify(w.submission_data, null, 2));
        }
        if (w.submissionData) {
          console.log(`  SubmissionData keys:`, Object.keys(w.submissionData));
          console.log(`  SubmissionData:`, JSON.stringify(w.submissionData, null, 2));
        }
        console.log(`  loadInDate:`, w.loadInDate || w.load_in_date);
        console.log(`  loadOutDate:`, w.loadOutDate || w.load_out_date);
        console.log(`  venueName:`, w.venueName || w.venue_name);
        console.log(`  venueAddress:`, w.venueAddress || w.venue_address);
      });
      console.log('=== END DEBUG ===');

      // Sort by updated date (most recent first)
      this.wishlists.sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at || 0);
        const dateB = new Date(b.updated_at || b.created_at || 0);
        return dateB - dateA;
      });

      if (loading) loading.style.display = 'none';

      if (this.wishlists.length === 0) {
        if (empty) {
          empty.style.display = 'block';
          const emptyMessage = empty.querySelector('[data-empty-message]');
          if (emptyMessage) {
            emptyMessage.textContent = 'No wishlists yet. Create one to get started!';
          }
        }
      } else {
        if (wishlistsContainer) {
          wishlistsContainer.style.display = 'grid';
          this.renderWishlists();
        }
      }
    } catch (error) {
      console.error('Failed to load wishlists:', error);
      if (loading) loading.style.display = 'none';
      if (empty) {
        empty.style.display = 'block';
        empty.innerHTML = '<p>Failed to load wishlists. Please refresh the page.</p>';
      }
    }
  }

  renderWishlists() {
    const container = document.querySelector('[data-wishlists]');
    if (!container) return;
    
    container.innerHTML = '';

    // Group by status for optional visual grouping
    const grouped = {
      draft: [],
      submitted: [],
      archived: []
    };

    for (const wishlist of this.wishlists) {
      const status = wishlist.status || 'draft';
      if (grouped[status]) {
        grouped[status].push(wishlist);
      } else {
        grouped.draft.push(wishlist);
      }
    }

    // Render cards grouped by status (optional - can be removed if single list preferred)
    let hasRendered = false;
    for (const [status, wishlists] of Object.entries(grouped)) {
      if (wishlists.length > 0) {
        // Optional: Add section header for visual grouping
        // Uncomment if you want section headers:
        // const sectionHeader = document.createElement('h2');
        // sectionHeader.className = 'wishlist-index__section-header';
        // sectionHeader.textContent = status.charAt(0).toUpperCase() + status.slice(1);
        // container.appendChild(sectionHeader);

        for (const wishlist of wishlists) {
          const card = this.createIndexCard(wishlist);
          container.appendChild(card);
          hasRendered = true;
        }
      }
    }

    if (!hasRendered) {
      container.style.display = 'none';
    }
  }

  createIndexCard(wishlist) {
    const template = document.getElementById('wishlist-index-card-template');
    if (!template) {
      console.error('Wishlist index card template not found');
      return document.createElement('div');
    }
    
    const card = template.content.cloneNode(true).querySelector('.wishlist-index-card');
    if (!card) {
      console.error('Card element not found in template');
      return document.createElement('div');
    }
    
    card.setAttribute('data-wishlist-id', wishlist.id);
    card.setAttribute('href', '#');
    card.setAttribute('data-wishlist-link', wishlist.id);
    // Add status attribute for CSS styling
    const status = wishlist.status || 'draft';
    card.setAttribute('data-status', status);
    
    // Name (primary, largest)
    const nameEl = card.querySelector('[data-wishlist-name]');
    if (nameEl) nameEl.textContent = wishlist.name;
    
    // Status badge (informational, subtle)
    const statusEl = card.querySelector('[data-wishlist-status]');
    if (statusEl) {
      const status = wishlist.status || 'draft';
      statusEl.textContent = status.charAt(0).toUpperCase() + status.slice(1);
      statusEl.className = `wishlist-index-card__status wishlist-index-card__status--${status}`;
    }
    
    // Format date range helper
    // If load-in and load-out dates match, show single date
    // Otherwise show date range
    const formatDateRange = (startDate, endDate) => {
      if (!startDate) return null;
      
      try {
        const start = new Date(startDate);
        if (isNaN(start.getTime())) return null;
        
        const end = endDate ? new Date(endDate) : null;
        
        const formatDate = (date) => {
          const month = date.toLocaleDateString('en-US', { month: 'long' });
          const day = date.getDate();
          const year = date.getFullYear();
          return `${month} ${day}, ${year}`;
        };
        
        // If end date exists and is different from start, show range
        if (end && !isNaN(end.getTime())) {
          // Check if dates are the same day (ignoring time)
          const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
          const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
          
          if (startDay.getTime() === endDay.getTime()) {
            // Dates match - show single date
            return formatDate(start);
          } else {
            // Date range
            const startMonth = start.toLocaleDateString('en-US', { month: 'long' });
            const startDayNum = start.getDate();
            const endMonth = end.toLocaleDateString('en-US', { month: 'long' });
            const endDayNum = end.getDate();
            const year = start.getFullYear();
            
            if (startMonth === endMonth && start.getFullYear() === end.getFullYear()) {
              return `${startMonth} ${startDayNum}–${endDayNum}, ${year}`;
            } else if (start.getFullYear() === end.getFullYear()) {
              return `${startMonth} ${startDayNum} – ${endMonth} ${endDayNum}, ${year}`;
            } else {
              return `${formatDate(start)} – ${formatDate(end)}`;
            }
          }
        } else {
          // Single date
          return formatDate(start);
        }
      } catch (error) {
        console.warn('Error formatting date:', error);
        return null;
      }
    };
    
    // Project Date(s) - secondary metadata
    // Date and venue are collected during submission, so check submission data first
    const eventDateEl = card.querySelector('[data-event-date]');
    if (eventDateEl) {
      // Check submission object first (submission data is where dates/venue are stored)
      const submission = wishlist.submission || wishlist.submission_data || wishlist.submissionData || {};
      
      // Try to find date fields by checking ALL properties (case-insensitive search)
      let loadInDate = null;
      let loadOutDate = null;
      
      // Backend returns snake_case: load_in_date, load_out_date, venue_name, venue_address
      // Check these fields first
      loadInDate = wishlist.load_in_date || null;
      loadOutDate = wishlist.load_out_date || null;
      
      // Fallback: check root level with pattern matching
      if (!loadInDate || !loadOutDate) {
        for (const key in wishlist) {
          const lowerKey = key.toLowerCase();
          if (lowerKey.includes('load') && lowerKey.includes('in') && !lowerKey.includes('out') && !lowerKey.includes('time')) {
            loadInDate = loadInDate || wishlist[key];
          }
          if (lowerKey.includes('load') && lowerKey.includes('out') && !lowerKey.includes('time')) {
            loadOutDate = loadOutDate || wishlist[key];
          }
        }
      }
      
      // Check submission object
      if (submission && typeof submission === 'object') {
        for (const key in submission) {
          const lowerKey = key.toLowerCase();
          if (lowerKey.includes('load') && lowerKey.includes('in') && !lowerKey.includes('out')) {
            loadInDate = loadInDate || submission[key];
          }
          if (lowerKey.includes('load') && lowerKey.includes('out')) {
            loadOutDate = loadOutDate || submission[key];
          }
        }
      }
      
      // Also try common field name variations
      loadInDate = loadInDate || wishlist.loadInDate || wishlist.load_in_date || 
                   wishlist.loadIn || wishlist.load_in ||
                   submission?.loadInDate || submission?.load_in_date ||
                   submission?.loadIn || submission?.load_in;
      
      loadOutDate = loadOutDate || wishlist.loadOutDate || wishlist.load_out_date ||
                   wishlist.loadOut || wishlist.load_out ||
                   submission?.loadOutDate || submission?.load_out_date ||
                   submission?.loadOut || submission?.load_out;
      
      // Debug logging for submitted wishlists
      if ((wishlist.status === 'submitted' || wishlist.submitted_at) && !loadInDate && !loadOutDate) {
        console.log('❌ No date data found for submitted wishlist:', wishlist.id, wishlist.name);
        console.log('   Submission object:', submission);
        console.log('   Submission object keys:', submission && typeof submission === 'object' ? Object.keys(submission) : 'not an object');
        console.log('   Submission object type:', typeof submission);
        console.log('   All wishlist keys:', Object.keys(wishlist));
        // Show ALL properties with their VALUES
        console.log('   ALL WISHLIST PROPERTIES:');
        Object.keys(wishlist).forEach(key => {
          const value = wishlist[key];
          const valueType = typeof value;
          let displayValue = value;
          if (valueType === 'object' && value !== null) {
            displayValue = JSON.stringify(value);
          }
          console.log(`     ${key} (${valueType}):`, displayValue);
        });
        if (submission && typeof submission === 'object') {
          console.log('   All submission properties:', Object.keys(submission).map(k => `${k}: ${typeof submission[k]} = ${JSON.stringify(submission[k])}`));
        }
      } else if (loadInDate || loadOutDate) {
        console.log('✅ Found date data for wishlist:', wishlist.id, 'Dates:', loadInDate, loadOutDate);
      }
      
      // Format date range (if dates match, show single date)
      const dateRange = formatDateRange(loadInDate, loadOutDate);
      // Fall back to eventDate if no load dates
      const dateStr = dateRange || (wishlist.eventDate ? formatDateRange(wishlist.eventDate, null) : null);
      
      if (dateStr) {
        eventDateEl.textContent = dateStr;
        eventDateEl.closest('.wishlist-index-card__meta-item').style.display = '';
        console.log(`✅ Displaying date for ${wishlist.name}:`, dateStr);
      } else {
        eventDateEl.closest('.wishlist-index-card__meta-item').style.display = 'none';
        if (wishlist.status === 'submitted' || wishlist.submitted_at) {
          console.log(`⚠️ No date to display for submitted wishlist ${wishlist.name}`);
        }
      }
    }
    
    // Venue - secondary metadata (same visual weight as dates)
    // Venue is collected during submission, so check submission data first
    const locationEl = card.querySelector('[data-location]');
    if (locationEl) {
      // Check submission object first (submission data is where dates/venue are stored)
      const submission = wishlist.submission || wishlist.submission_data || wishlist.submissionData || {};
      
      // Try to find venue fields by checking ALL properties (case-insensitive search)
      let venueName = null;
      let venueAddress = null;
      
      // Backend returns snake_case: venue_name, venue_address
      // Check these fields first
      venueName = wishlist.venue_name || null;
      venueAddress = wishlist.venue_address || null;
      
      // Fallback: check root level with pattern matching
      if (!venueName || !venueAddress) {
        for (const key in wishlist) {
          const lowerKey = key.toLowerCase();
          if (lowerKey.includes('venue') && !lowerKey.includes('address')) {
            venueName = venueName || wishlist[key];
          }
          if (lowerKey.includes('venue') && lowerKey.includes('address')) {
            venueAddress = venueAddress || wishlist[key];
          }
          if (lowerKey === 'location' && !venueAddress) {
            venueAddress = venueAddress || wishlist[key];
          }
        }
      }
      
      // Check submission object
      if (submission && typeof submission === 'object') {
        for (const key in submission) {
          const lowerKey = key.toLowerCase();
          if (lowerKey.includes('venue') && !lowerKey.includes('address')) {
            venueName = venueName || submission[key];
          }
          if (lowerKey.includes('venue') && lowerKey.includes('address')) {
            venueAddress = venueAddress || submission[key];
          }
        }
      }
      
      // Also try common field name variations
      venueName = venueName || wishlist.venueName || wishlist.venue_name || wishlist.venue ||
                 submission?.venueName || submission?.venue_name || submission?.venue;
      venueAddress = venueAddress || wishlist.venueAddress || wishlist.venue_address || wishlist.location ||
                    submission?.venueAddress || submission?.venue_address;
      
      // Debug logging for submitted wishlists
      if ((wishlist.status === 'submitted' || wishlist.submitted_at) && !venueName && !venueAddress) {
        console.log('❌ No venue data found for submitted wishlist:', wishlist.id, wishlist.name);
        console.log('   Submission object:', submission);
        console.log('   Submission object keys:', submission && typeof submission === 'object' ? Object.keys(submission) : 'not an object');
        console.log('   Submission object type:', typeof submission);
        if (submission && typeof submission === 'object') {
          console.log('   All submission properties:', Object.keys(submission).map(k => `${k}: ${typeof submission[k]} = ${JSON.stringify(submission[k])}`));
        }
      } else if (venueName || venueAddress) {
        console.log('✅ Found venue data for wishlist:', wishlist.id, 'Venue:', venueName || venueAddress);
      }
      
      if (venueName || venueAddress) {
        let venueDisplay = '';
        
        // Prefer venue name, fallback to address
        if (venueName) {
          venueDisplay = venueName;
        } else if (venueAddress) {
          // Remove ", USA" or ", United States" from address
          venueDisplay = venueAddress
            .replace(/,\s*USA\s*$/i, '')
            .replace(/,\s*United States\s*$/i, '')
            .trim();
        }
        
        if (venueDisplay) {
          locationEl.textContent = venueDisplay;
          locationEl.closest('.wishlist-index-card__meta-item').style.display = '';
          console.log(`✅ Displaying venue for ${wishlist.name}:`, venueDisplay);
        } else {
          locationEl.closest('.wishlist-index-card__meta-item').style.display = 'none';
        }
      } else {
        locationEl.closest('.wishlist-index-card__meta-item').style.display = 'none';
        if (wishlist.status === 'submitted' || wishlist.submitted_at) {
          console.log(`⚠️ No venue to display for submitted wishlist ${wishlist.name}`);
        }
      }
    }
    
    // Item Count - tertiary, small and quiet
    const itemCountEl = card.querySelector('[data-item-count]');
    if (itemCountEl) {
      const count = wishlist.itemCount || (wishlist.items ? wishlist.items.length : 0);
      if (count > 0) {
        itemCountEl.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
        itemCountEl.style.display = '';
      } else {
        itemCountEl.style.display = 'none';
      }
    }
    
    // Kebab menu
    const menuToggle = card.querySelector('[data-wishlist-menu-toggle]');
    const menuDropdown = card.querySelector('[data-wishlist-menu-dropdown]');
    
    if (menuToggle && menuDropdown) {
      menuToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const isOpen = menuDropdown.classList.contains('is-open');
        // Close all other menus
        document.querySelectorAll('.wishlist-index-card__menu-dropdown.is-open').forEach(menu => {
          menu.classList.remove('is-open');
        });
        // Toggle this menu
        menuDropdown.classList.toggle('is-open', !isOpen);
      });
      
      // Close menu when clicking outside
      document.addEventListener('click', (e) => {
        if (!card.contains(e.target)) {
          menuDropdown.classList.remove('is-open');
        }
      });
    }
    
    const renameBtn = card.querySelector('[data-rename-wishlist]');
    if (renameBtn) {
      renameBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        menuDropdown.classList.remove('is-open');
        this.renameWishlist(wishlist.id, wishlist.name);
      });
    }

    const duplicateBtn = card.querySelector('[data-duplicate-wishlist]');
    if (duplicateBtn) {
      duplicateBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        menuDropdown.classList.remove('is-open');
        this.duplicateWishlist(wishlist.id);
      });
    }

    const archiveBtn = card.querySelector('[data-archive-wishlist]');
    if (archiveBtn) {
      archiveBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        menuDropdown.classList.remove('is-open');
        this.archiveWishlist(wishlist.id, wishlist.status === 'archived');
      });
    }

    const deleteBtn = card.querySelector('[data-delete-wishlist]');
    if (deleteBtn) {
      deleteBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        menuDropdown.classList.remove('is-open');
        this.deleteWishlist(wishlist.id);
      });
    }
    
    // Handle card click - navigate to detail view
    const handleCardClick = (e) => {
      // Don't navigate if clicking menu or menu items
      if (e.target.closest('[data-wishlist-menu]')) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      
      // Prevent default link navigation
      e.preventDefault();
      e.stopPropagation();
      
      // Navigate to detail view
      const wishlistId = card.getAttribute('data-wishlist-link') || wishlist.id;
      console.log('Opening wishlist detail:', wishlistId);
      
      if (!wishlistId) {
        console.error('No wishlist ID found');
        return false;
      }
      
      window.history.pushState({}, '', `/pages/wishlist?id=${wishlistId}`);
      this.viewMode = 'detail';
      this.showWishlistDetail(wishlistId);
      return false;
    };
    
    card.addEventListener('click', handleCardClick);

    return card;
  }

  async showWishlistDetail(wishlistId) {
    try {
      console.log('Loading wishlist detail for ID:', wishlistId);
      const wishlist = await this.api.getWishlist(wishlistId);
      if (!wishlist) {
        console.error('Wishlist not found:', wishlistId);
        this.showError('Wishlist not found');
        window.history.pushState({}, '', '/pages/wishlist');
        this.viewMode = 'index';
        this.loadWishlists();
        return;
      }

      console.log('Wishlist loaded:', wishlist);
      this.currentWishlist = wishlist;
      this.renderWishlistDetail(wishlist);
    } catch (error) {
      console.error('Failed to load wishlist:', error);
      this.showError('Failed to load wishlist');
      window.history.pushState({}, '', '/pages/wishlist');
      this.viewMode = 'index';
      this.loadWishlists();
    }
  }

  renderWishlistDetail(wishlist) {
    const content = document.querySelector('[data-wishlist-content]');
    if (!content) {
      console.error('Wishlist content container not found');
      return;
    }

    // Hide index view
    content.style.display = 'none';

    // Create detail container if it doesn't exist
    let detailContainer = document.querySelector('[data-wishlist-detail]');
    if (!detailContainer) {
      detailContainer = document.createElement('div');
      detailContainer.setAttribute('data-wishlist-detail', '');
      const wrapper = document.querySelector('.wishlist-page .wrapper');
      if (wrapper) {
        wrapper.appendChild(detailContainer);
      } else {
        content.parentNode.insertBefore(detailContainer, content.nextSibling);
      }
    }

    const template = document.getElementById('wishlist-detail-template');
    if (!template) {
      console.error('Wishlist detail template not found');
      return;
    }

    detailContainer.innerHTML = '';
    const wrapper = template.content.cloneNode(true).querySelector('.wishlist-detail-wrapper');
    const card = wrapper.querySelector('.wishlist-card');
    card.setAttribute('data-wishlist-id', wishlist.id);

    // Use existing createWishlistCard logic but adapt for detail view
    card.querySelector('[data-wishlist-name]').textContent = wishlist.name;
    
    // Status badge
    const statusEl = card.querySelector('[data-wishlist-status]');
    if (statusEl) {
      const status = wishlist.status || 'draft';
      statusEl.textContent = status.charAt(0).toUpperCase() + status.slice(1);
      statusEl.className = `wishlist-card__status wishlist-card__status--${status}`;
    }

    // Meta info
    const meta = card.querySelector('.wishlist-card__meta');
    const metaParts = [];
    if (wishlist.eventDate) {
      metaParts.push(`Event: ${new Date(wishlist.eventDate).toLocaleDateString()}`);
    }
    if (wishlist.location) {
      metaParts.push(`Location: ${wishlist.location}`);
    }
    metaParts.push(`${wishlist.itemCount || 0} items`);
    meta.innerHTML = metaParts.join(' • ');

    // Items
    const itemsContainer = card.querySelector('[data-items]');
    if (wishlist.items && wishlist.items.length > 0) {
      const grouped = this.groupItemsByPlacement(wishlist.items);
      
      for (const [placement, items] of Object.entries(grouped)) {
        const placementHeader = document.createElement('h4');
        placementHeader.className = 'wishlist-card__placement-header';
        placementHeader.textContent = placement || 'Unassigned';
        itemsContainer.appendChild(placementHeader);

        for (const item of items) {
          const itemRow = this.createItemRow(item, wishlist.id);
          itemsContainer.appendChild(itemRow);
        }
      }
    } else {
      itemsContainer.innerHTML = '<p class="wishlist-card__empty">No items yet. Add items from product pages!</p>';
    }

    // Menu actions
    const menuToggle = card.querySelector('[data-wishlist-menu-toggle]');
    const menuDropdown = card.querySelector('[data-wishlist-menu-dropdown]');
    
    if (menuToggle && menuDropdown) {
      menuToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = menuDropdown.classList.contains('is-open');
        document.querySelectorAll('.wishlist-card__menu-dropdown.is-open').forEach(menu => {
          menu.classList.remove('is-open');
        });
        menuDropdown.classList.toggle('is-open', !isOpen);
      });
      
      document.addEventListener('click', (e) => {
        if (!card.contains(e.target)) {
          menuDropdown.classList.remove('is-open');
        }
      });
    }
    
    const renameBtn = card.querySelector('[data-rename-wishlist]');
    if (renameBtn) {
      renameBtn.addEventListener('click', () => {
        menuDropdown.classList.remove('is-open');
        this.renameWishlist(wishlist.id, wishlist.name);
      });
    }

    const duplicateBtn = card.querySelector('[data-duplicate-wishlist]');
    if (duplicateBtn) {
      duplicateBtn.addEventListener('click', () => {
        menuDropdown.classList.remove('is-open');
        this.duplicateWishlist(wishlist.id);
      });
    }

    const archiveBtn = card.querySelector('[data-archive-wishlist]');
    if (archiveBtn) {
      archiveBtn.addEventListener('click', () => {
        menuDropdown.classList.remove('is-open');
        this.archiveWishlist(wishlist.id, wishlist.status === 'archived');
      });
    }

    const deleteBtn = card.querySelector('[data-delete-wishlist]');
    if (deleteBtn) {
      deleteBtn.addEventListener('click', () => {
        menuDropdown.classList.remove('is-open');
        this.deleteWishlist(wishlist.id);
      });
    }

    const submitBtn = card.querySelector('[data-submit-wishlist]');
    const wasSubmitted = wishlist.status === 'submitted' || wishlist.submitted_at;
    
    if (wishlist.status === 'submitted') {
      submitBtn.textContent = 'Resubmit';
      submitBtn.setAttribute('data-was-submitted', 'true');
    } else if (wasSubmitted) {
      submitBtn.textContent = 'Resubmit';
      submitBtn.setAttribute('data-was-submitted', 'true');
    } else {
      submitBtn.textContent = 'Submit for Proposal';
      submitBtn.removeAttribute('data-was-submitted');
    }
    
    submitBtn.addEventListener('click', () => this.submitWishlist(wishlist.id));

    // Back button
    const backBtn = wrapper.querySelector('[data-back-button]');
    if (backBtn) {
      backBtn.addEventListener('click', (e) => {
        e.preventDefault();
        window.history.pushState({}, '', '/pages/wishlist');
        this.viewMode = 'index';
        detailContainer.style.display = 'none';
        content.style.display = 'block';
        this.loadWishlists();
      });
    }

    detailContainer.appendChild(wrapper);
    detailContainer.style.display = 'block';
    console.log('Detail view rendered');
  }

  createWishlistCard(wishlist) {
    const template = document.getElementById('wishlist-item-template');
    const card = template.content.cloneNode(true).querySelector('.wishlist-card');
    
    card.setAttribute('data-wishlist-id', wishlist.id);
    card.querySelector('[data-wishlist-name]').textContent = wishlist.name;
    
    // Meta info
    const meta = card.querySelector('.wishlist-card__meta');
    const metaParts = [];
    if (wishlist.eventDate) {
      metaParts.push(`Event: ${new Date(wishlist.eventDate).toLocaleDateString()}`);
    }
    if (wishlist.location) {
      metaParts.push(`Location: ${wishlist.location}`);
    }
    metaParts.push(`${wishlist.itemCount || 0} items`);
    meta.innerHTML = metaParts.join(' • ');

    // Items
    const itemsContainer = card.querySelector('[data-items]');
    if (wishlist.items && wishlist.items.length > 0) {
      // Group by placement
      const grouped = this.groupItemsByPlacement(wishlist.items);
      
      for (const [placement, items] of Object.entries(grouped)) {
        const placementHeader = document.createElement('h4');
        placementHeader.className = 'wishlist-card__placement-header';
        placementHeader.textContent = placement || 'Unassigned';
        itemsContainer.appendChild(placementHeader);

        for (const item of items) {
          const itemRow = this.createItemRow(item, wishlist.id);
          itemsContainer.appendChild(itemRow);
        }
      }
    } else {
      itemsContainer.innerHTML = '<p class="wishlist-card__empty">No items yet. Add items from product pages!</p>';
    }

    // Actions - Kebab menu
    const menuToggle = card.querySelector('[data-wishlist-menu-toggle]');
    const menuDropdown = card.querySelector('[data-wishlist-menu-dropdown]');
    
    if (menuToggle && menuDropdown) {
      menuToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = menuDropdown.classList.contains('is-open');
        // Close all other menus
        document.querySelectorAll('.wishlist-card__menu-dropdown.is-open').forEach(menu => {
          menu.classList.remove('is-open');
        });
        // Toggle this menu
        menuDropdown.classList.toggle('is-open', !isOpen);
      });
      
      // Close menu when clicking outside
      document.addEventListener('click', (e) => {
        if (!card.contains(e.target)) {
          menuDropdown.classList.remove('is-open');
        }
      });
    }
    
    const renameBtn = card.querySelector('[data-rename-wishlist]');
    renameBtn.addEventListener('click', () => {
      menuDropdown.classList.remove('is-open');
      this.renameWishlist(wishlist.id, wishlist.name);
    });

    const duplicateBtn = card.querySelector('[data-duplicate-wishlist]');
    duplicateBtn.addEventListener('click', () => {
      menuDropdown.classList.remove('is-open');
      this.duplicateWishlist(wishlist.id);
    });

    const archiveBtn = card.querySelector('[data-archive-wishlist]');
    archiveBtn.addEventListener('click', () => {
      menuDropdown.classList.remove('is-open');
      this.archiveWishlist(wishlist.id, wishlist.status === 'archived');
    });

    const deleteBtn = card.querySelector('[data-delete-wishlist]');
    if (deleteBtn) {
      deleteBtn.addEventListener('click', () => {
        menuDropdown.classList.remove('is-open');
        this.deleteWishlist(wishlist.id);
      });
    }

    const submitBtn = card.querySelector('[data-submit-wishlist]');
    // Store original status for resubmit detection
    const wasSubmitted = wishlist.status === 'submitted' || wishlist.submitted_at;
    
    if (wishlist.status === 'submitted') {
      // Submitted wishlists can be resubmitted if edited
      submitBtn.textContent = 'Resubmit';
      submitBtn.setAttribute('data-was-submitted', 'true');
    } else if (wasSubmitted) {
      // Was submitted but now draft (edited)
      submitBtn.textContent = 'Resubmit';
      submitBtn.setAttribute('data-was-submitted', 'true');
    } else {
      submitBtn.textContent = 'Submit for Proposal';
      submitBtn.removeAttribute('data-was-submitted');
    }
    
    submitBtn.addEventListener('click', () => this.submitWishlist(wishlist.id));

    return card;
  }

  createItemRow(item, wishlistId) {
    const template = document.getElementById('wishlist-item-row-template');
    if (!template) {
      console.error('Wishlist item row template not found');
      return document.createElement('div'); // Return empty div as fallback
    }
    
    const row = template.content.cloneNode(true).querySelector('.wishlist-item');
    if (!row) {
      console.error('Failed to clone wishlist item row template');
      return document.createElement('div'); // Return empty div as fallback
    }
    
    row.setAttribute('data-item-id', item.id);
    
    // Image
    const img = row.querySelector('[data-item-image]');
    if (img) {
      if (item.imageUrl) {
        img.src = item.imageUrl;
        img.alt = item.productTitle || '';
      } else {
        // Fallback placeholder
        img.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="64" height="64"%3E%3Crect width="64" height="64" fill="%23f0f0f0"/%3E%3C/svg%3E';
        img.alt = '';
      }
    }
    
    // Title
    const titleElement = row.querySelector('[data-item-title]');
    if (titleElement) {
      titleElement.textContent = item.productTitle || '';
      // Only append variant title if it exists and is not "Default Title"
      if (item.variantTitle && 
          item.variantTitle.toLowerCase() !== 'default title' && 
          item.variantTitle.trim() !== '') {
        titleElement.textContent += ` - ${item.variantTitle}`;
      }
    }

    // Meta (SKU)
    const meta = row.querySelector('[data-item-meta]');
    if (meta) {
      if (item.sku) {
        meta.textContent = `SKU: ${item.sku}`;
      } else {
        meta.style.display = 'none';
      }
    }

    // Quantity with stepper
    const qtyValue = row.querySelector('[data-item-quantity]');
    const qtyDecrease = row.querySelector('[data-quantity-decrease]');
    const qtyIncrease = row.querySelector('[data-quantity-increase]');
    
    if (!qtyValue) {
      console.error('Quantity value element not found in template');
      return row;
    }
    
    const updateQuantity = (newQty) => {
      const qty = Math.max(1, parseInt(newQty) || 1);
      qtyValue.textContent = qty;
      
      // Update button states
      if (qtyDecrease) {
        qtyDecrease.disabled = qty <= 1;
      }
      
      this.updateItem(wishlistId, item.id, { quantity: qty });
    };
    
    // Set initial quantity and button state
    qtyValue.textContent = item.quantity || 1;
    if (qtyDecrease) {
      qtyDecrease.disabled = (item.quantity || 1) <= 1;
    }
    
    if (qtyDecrease) {
      qtyDecrease.addEventListener('click', () => {
        const currentQty = parseInt(qtyValue.textContent) || 1;
        if (currentQty > 1) {
          updateQuantity(currentQty - 1);
        }
      });
    }
    
    if (qtyIncrease) {
      qtyIncrease.addEventListener('click', () => {
        const currentQty = parseInt(qtyValue.textContent) || 1;
        updateQuantity(currentQty + 1);
      });
    }

    // Notes - toggle drawer
    const notesToggle = row.querySelector('[data-notes-toggle]');
    const notesContainer = row.querySelector('[data-notes-container]');
    const notesTextarea = row.querySelector('[data-item-notes]');
    
    if (notesToggle && notesContainer && notesTextarea) {
      const hasNotes = item.notes && item.notes.trim();
      const notesLabel = notesToggle.querySelector('[data-notes-label-text]');
      
      if (hasNotes) {
        notesTextarea.value = item.notes;
        // Show preview in toggle button
        if (notesLabel) {
          const preview = item.notes.length > 30 ? item.notes.substring(0, 30) + '...' : item.notes;
          notesLabel.textContent = preview;
        }
        notesContainer.style.display = 'none';
        notesToggle.style.display = 'flex';
      } else {
        notesTextarea.value = '';
        if (notesLabel) {
          notesLabel.textContent = 'Add note';
        }
        notesContainer.style.display = 'none';
        notesToggle.style.display = 'flex';
      }
      
      notesToggle.addEventListener('click', () => {
        notesContainer.style.display = 'block';
        notesToggle.style.display = 'none';
        setTimeout(() => notesTextarea.focus(), 10);
      });
      
      notesTextarea.addEventListener('blur', () => {
        const hasContent = notesTextarea.value && notesTextarea.value.trim();
        if (hasContent) {
          // Update preview
          if (notesLabel) {
            const preview = notesTextarea.value.length > 30 ? notesTextarea.value.substring(0, 30) + '...' : notesTextarea.value;
            notesLabel.textContent = preview;
          }
          notesContainer.style.display = 'none';
          notesToggle.style.display = 'flex';
        } else {
          if (notesLabel) {
            notesLabel.textContent = 'Add note';
          }
          notesContainer.style.display = 'none';
          notesToggle.style.display = 'flex';
        }
        this.updateItem(wishlistId, item.id, { notes: notesTextarea.value || null });
      });
    }

    // Remove
    const removeBtn = row.querySelector('[data-remove-item]');
    if (removeBtn) {
      removeBtn.addEventListener('click', () => {
        this.removeItem(wishlistId, item.id);
      });
    }

    return row;
  }

  groupItemsByPlacement(items) {
    const grouped = {};
    for (const item of items) {
      const placement = item.placement || 'Unassigned';
      if (!grouped[placement]) {
        grouped[placement] = [];
      }
      grouped[placement].push(item);
    }
    return grouped;
  }

  async updateItem(wishlistId, itemId, updates) {
    try {
      await this.api.updateItem(wishlistId, itemId, updates);
      // If wishlist was submitted, change it back to draft when edited
      await this.checkAndResetSubmittedStatus(wishlistId);
      // Reload wishlists to reflect changes
      await this.loadWishlists();
    } catch (error) {
      console.error('Failed to update item:', error);
      if (window.WishlistDialogs) {
        await window.WishlistDialogs.alert('Failed to update item. Please try again.', 'Error');
      } else {
        alert('Failed to update item. Please try again.');
      }
    }
  }

  async removeItem(wishlistId, itemId) {
    if (!window.WishlistDialogs) {
      if (!confirm('Remove this item from the wishlist?')) return;
    } else {
      const confirmed = await window.WishlistDialogs.confirm('Remove this item from the wishlist?', 'Remove Item');
      if (!confirmed) return;
    }

    try {
      await this.api.removeItem(wishlistId, itemId);
      // If wishlist was submitted, change it back to draft when edited
      await this.checkAndResetSubmittedStatus(wishlistId);
      await this.loadWishlists();
    } catch (error) {
      console.error('Failed to remove item:', error);
      if (window.WishlistDialogs) {
        await window.WishlistDialogs.alert('Failed to remove item. Please try again.', 'Error');
      } else {
        alert('Failed to remove item. Please try again.');
      }
    }
  }

  /**
   * Check if wishlist is submitted and reset to draft if edited
   */
  async checkAndResetSubmittedStatus(wishlistId) {
    try {
      const wishlist = this.wishlists.find(w => w.id === wishlistId);
      if (wishlist && wishlist.status === 'submitted') {
        // Change status back to draft when edited
        await this.api.updateWishlist(wishlistId, { status: 'draft' });
      }
    } catch (error) {
      console.error('Failed to reset submitted status:', error);
      // Don't show error to user, this is a background operation
    }
  }

  async renameWishlist(wishlistId, currentName) {
    let newName;
    if (window.WishlistDialogs) {
      newName = await window.WishlistDialogs.prompt('Enter new wishlist name:', currentName, 'Rename Wishlist');
    } else {
      newName = prompt('Enter new wishlist name:', currentName);
    }
    
    if (!newName || newName === currentName) {
      return;
    }

    try {
      await this.api.updateWishlist(wishlistId, { name: newName });
      if (this.viewMode === 'detail' && this.currentWishlist && this.currentWishlist.id === wishlistId) {
        this.currentWishlist.name = newName;
        this.renderWishlistDetail(this.currentWishlist);
      }
      await this.loadWishlists();
    } catch (error) {
      console.error('Failed to rename wishlist:', error);
      if (window.WishlistDialogs) {
        await window.WishlistDialogs.alert('Failed to rename wishlist. Please try again.', 'Error');
      } else {
        alert('Failed to rename wishlist. Please try again.');
      }
    }
  }

  async duplicateWishlist(wishlistId) {
    let confirmed;
    if (window.WishlistDialogs) {
      confirmed = await window.WishlistDialogs.confirm('Create a copy of this wishlist?', 'Duplicate Wishlist');
    } else {
      confirmed = confirm('Create a copy of this wishlist?');
    }
    
    if (!confirmed) {
      return;
    }

    try {
      await this.api.duplicateWishlist(wishlistId);
      await this.loadWishlists();
      if (window.WishlistDialogs) {
        await window.WishlistDialogs.alert('Wishlist duplicated successfully!', 'Success');
      } else {
        alert('Wishlist duplicated successfully!');
      }
    } catch (error) {
      console.error('Failed to duplicate wishlist:', error);
      if (window.WishlistDialogs) {
        await window.WishlistDialogs.alert('Failed to duplicate wishlist. Please try again.', 'Error');
      } else {
        alert('Failed to duplicate wishlist. Please try again.');
      }
    }
  }

  async archiveWishlist(wishlistId, isArchived) {
    const action = isArchived ? 'unarchive' : 'archive';
    const actionTitle = action.charAt(0).toUpperCase() + action.slice(1);
    
    let confirmed;
    if (window.WishlistDialogs) {
      confirmed = await window.WishlistDialogs.confirm(`${actionTitle} this wishlist?`, `${actionTitle} Wishlist`);
    } else {
      confirmed = confirm(`${actionTitle} this wishlist?`);
    }
    
    if (!confirmed) {
      return;
    }

    try {
      await this.api.archiveWishlist(wishlistId, !isArchived);
      await this.loadWishlists();
    } catch (error) {
      console.error('Failed to archive wishlist:', error);
      if (window.WishlistDialogs) {
        await window.WishlistDialogs.alert('Failed to archive wishlist. Please try again.', 'Error');
      } else {
        alert('Failed to archive wishlist. Please try again.');
      }
    }
  }

  async deleteWishlist(wishlistId) {
    let confirmed;
    if (window.WishlistDialogs) {
      confirmed = await window.WishlistDialogs.confirm(
        'Are you sure you want to delete this wishlist? This action cannot be undone.',
        'Delete Wishlist'
      );
    } else {
      confirmed = confirm('Are you sure you want to delete this wishlist? This action cannot be undone.');
    }
    
    if (!confirmed) {
      return;
    }

    try {
      await this.api.deleteWishlist(wishlistId);
      
      // If we're in detail view, navigate back to index
      if (this.viewMode === 'detail') {
        window.history.pushState({}, '', '/pages/wishlist');
        this.viewMode = 'index';
        const detailContainer = document.querySelector('[data-wishlist-detail]');
        const content = document.querySelector('[data-wishlist-content]');
        if (detailContainer) detailContainer.style.display = 'none';
        if (content) content.style.display = 'block';
      }
      
      await this.loadWishlists();
      
      if (window.WishlistDialogs) {
        await window.WishlistDialogs.alert('Wishlist deleted successfully.', 'Success');
      } else {
        alert('Wishlist deleted successfully.');
      }
    } catch (error) {
      console.error('Failed to delete wishlist:', error);
      if (window.WishlistDialogs) {
        await window.WishlistDialogs.alert('Failed to delete wishlist. Please try again.', 'Error');
      } else {
        alert('Failed to delete wishlist. Please try again.');
      }
    }
  }

  async submitWishlist(wishlistId) {
    // Get wishlist data to pre-fill form if resubmitting
    // Need to fetch full wishlist to get submission data
    let wishlist = this.wishlists.find(w => w.id === wishlistId);
    
    // If wishlist is submitted, fetch full details to get submission data
    if (wishlist && (wishlist.status === 'submitted' || wishlist.submitted_at)) {
      try {
        const fullWishlistResponse = await this.api.getWishlist(wishlistId);
        const fullWishlist = fullWishlistResponse.wishlist || fullWishlistResponse;
        wishlist = { ...wishlist, ...fullWishlist };
      } catch (error) {
        console.warn('Failed to fetch full wishlist for resubmit:', error);
      }
    }
    
    // Extract submission data using the same logic as card display
    const submission = wishlist?.submission || wishlist?.submission_data || wishlist?.submissionData || {};
    
    // Try to find fields by checking ALL properties (same as card display)
    let loadInDate = null;
    let loadOutDate = null;
    let loadInTime = null;
    let loadOutTime = null;
    let venueName = null;
    let venueAddress = null;
    
      // Backend returns snake_case fields directly on wishlist
      // Check these fields first
      if (wishlist) {
        loadInDate = wishlist.load_in_date || null;
        loadOutDate = wishlist.load_out_date || null;
        loadInTime = wishlist.load_in_time || null;
        loadOutTime = wishlist.load_out_time || null;
        venueName = wishlist.venue_name || null;
        venueAddress = wishlist.venue_address || null;
        
        // Fallback: pattern matching for backwards compatibility
        if (!loadInDate || !loadOutDate || !loadInTime || !loadOutTime || !venueName || !venueAddress) {
          for (const key in wishlist) {
            const lowerKey = key.toLowerCase();
            if (lowerKey.includes('load') && lowerKey.includes('in') && !lowerKey.includes('out')) {
              if (lowerKey.includes('time')) {
                loadInTime = loadInTime || wishlist[key];
              } else {
                loadInDate = loadInDate || wishlist[key];
              }
            }
            if (lowerKey.includes('load') && lowerKey.includes('out')) {
              if (lowerKey.includes('time')) {
                loadOutTime = loadOutTime || wishlist[key];
              } else {
                loadOutDate = loadOutDate || wishlist[key];
              }
            }
            if (lowerKey.includes('venue') && !lowerKey.includes('address')) {
              venueName = venueName || wishlist[key];
            }
            if (lowerKey.includes('venue') && lowerKey.includes('address')) {
              venueAddress = venueAddress || wishlist[key];
            }
          }
        }
      }
    
    // Check submission object
    if (submission && typeof submission === 'object') {
      for (const key in submission) {
        const lowerKey = key.toLowerCase();
        if (lowerKey.includes('load') && lowerKey.includes('in') && !lowerKey.includes('out')) {
          if (lowerKey.includes('time')) {
            loadInTime = loadInTime || submission[key];
          } else {
            loadInDate = loadInDate || submission[key];
          }
        }
        if (lowerKey.includes('load') && lowerKey.includes('out')) {
          if (lowerKey.includes('time')) {
            loadOutTime = loadOutTime || submission[key];
          } else {
            loadOutDate = loadOutDate || submission[key];
          }
        }
        if (lowerKey.includes('venue') && !lowerKey.includes('address')) {
          venueName = venueName || submission[key];
        }
        if (lowerKey.includes('venue') && lowerKey.includes('address')) {
          venueAddress = venueAddress || submission[key];
        }
      }
    }
    
    // Also try common field name variations
    loadInDate = loadInDate || wishlist?.loadInDate || wishlist?.load_in_date || 
                 wishlist?.loadIn || wishlist?.load_in ||
                 submission?.loadInDate || submission?.load_in_date ||
                 submission?.loadIn || submission?.load_in;
    
    loadOutDate = loadOutDate || wishlist?.loadOutDate || wishlist?.load_out_date ||
                 wishlist?.loadOut || wishlist?.load_out ||
                 submission?.loadOutDate || submission?.load_out_date ||
                 submission?.loadOut || submission?.load_out;
    
    loadInTime = loadInTime || wishlist?.loadInTime || wishlist?.load_in_time ||
                submission?.loadInTime || submission?.load_in_time;
    
    loadOutTime = loadOutTime || wishlist?.loadOutTime || wishlist?.load_out_time ||
                 submission?.loadOutTime || submission?.load_out_time;
    
    venueName = venueName || wishlist?.venueName || wishlist?.venue_name || wishlist?.venue ||
               submission?.venueName || submission?.venue_name || submission?.venue;
    
    venueAddress = venueAddress || wishlist?.venueAddress || wishlist?.venue_address || wishlist?.location ||
                  submission?.venueAddress || submission?.venue_address;
    
    const previousData = wishlist ? {
      loadInDate,
      loadInTime,
      loadOutDate,
      loadOutTime,
      venueName,
      venueAddress
    } : null;
    
    console.log('📋 Previous submission data for resubmit:', previousData);
    console.log('   Submission object:', submission);
    console.log('   Submission object keys:', submission && typeof submission === 'object' ? Object.keys(submission) : 'not an object');
    console.log('   Submission object JSON:', JSON.stringify(submission, null, 2));
    console.log('   Wishlist keys:', wishlist ? Object.keys(wishlist) : 'no wishlist');
    
    // Show ALL wishlist properties with values
    if (wishlist) {
      console.log('   ALL WISHLIST PROPERTIES:');
      Object.keys(wishlist).forEach(key => {
        const value = wishlist[key];
        const valueType = typeof value;
        let displayValue = value;
        if (valueType === 'object' && value !== null) {
          displayValue = JSON.stringify(value);
        }
        console.log(`     ${key} (${valueType}):`, displayValue);
      });
    }
    
    // If no previous data found, log what we're looking for
    if (!previousData || (!previousData.loadInDate && !previousData.venueName)) {
      console.warn('⚠️ No previous submission data found!');
      console.warn('   Searched for:', {
        loadInDate: 'loadInDate, load_in_date, loadIn, load_in',
        loadOutDate: 'loadOutDate, load_out_date, loadOut, load_out',
        loadInTime: 'loadInTime, load_in_time',
        loadOutTime: 'loadOutTime, load_out_time',
        venueName: 'venueName, venue_name, venue',
        venueAddress: 'venueAddress, venue_address, location'
      });
    }
    
    // Show submission form modal with previous data
    const submissionData = await this.showSubmissionForm(previousData);
    
    if (!submissionData) {
      return; // User cancelled
    }

    const submitBtn = document.querySelector(`[data-wishlist-id="${wishlistId}"] [data-submit-wishlist]`);
    submitBtn.disabled = true;
    submitBtn.textContent = 'Submitting...';

    try {
      // Get user info
      const userResult = await this.api.request('/users/me');
      
      const result = await this.api.submitWishlist(wishlistId, {
        name: userResult.name,
        email: userResult.email,
        company: userResult.company,
        phone: userResult.phone,
        loadInDate: submissionData.loadInDate,
        loadInTime: submissionData.loadInTime,
        loadOutDate: submissionData.loadOutDate,
        loadOutTime: submissionData.loadOutTime,
        venueName: submissionData.venueName,
        venueAddress: submissionData.venueAddress,
        missingItems: submissionData.missingItems,
        projectNotes: submissionData.projectNotes
      });
      
      console.log('📤 Submit response:', result);
      console.log('   Response keys:', Object.keys(result));
      console.log('   Response JSON:', JSON.stringify(result, null, 2));

      if (window.WishlistDialogs) {
        await window.WishlistDialogs.alert('Wishlist submitted successfully! The events team will be in touch soon.', 'Success');
      } else {
        alert('Wishlist submitted successfully! The events team will be in touch soon.');
      }
      
      // Reload to show updated status
      if (this.viewMode === 'detail' && this.currentWishlist && this.currentWishlist.id === wishlistId) {
        await this.showWishlistDetail(wishlistId);
      }
      await this.loadWishlists();
    } catch (error) {
      console.error('Failed to submit wishlist:', error);
      submitBtn.disabled = false;
      submitBtn.textContent = 'Submit for Proposal';
      if (window.WishlistDialogs) {
        await window.WishlistDialogs.alert('Failed to submit wishlist. Please try again.', 'Error');
      } else {
        alert('Failed to submit wishlist. Please try again.');
      }
    }
  }

  /**
   * Show submission form modal with event details
   * @param {Object} previousData - Optional previous submission data to pre-fill form
   */
  async showSubmissionForm(previousData = null) {
    return new Promise((resolve) => {
      const modal = document.createElement('div');
      modal.className = 'wishlist-modal';
      modal.innerHTML = `
        <div class="wishlist-modal__overlay" data-modal-close></div>
        <div class="wishlist-modal__content wishlist-modal__content--large">
          <button class="wishlist-modal__close" data-modal-close aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2"/>
            </svg>
          </button>
          
          <form class="wishlist-submit-form" data-wishlist-submit-form>
            <h2 class="wishlist-submit-form__title">Submit Wishlist for Proposal</h2>
            <p class="wishlist-submit-form__subtitle">Please provide the following event details:</p>
            
            <div class="wishlist-submit-form__fields">
              <div class="wishlist-submit-form__row">
                <div class="wishlist-submit-form__field">
                  <label for="load-in-date">Load-In Date *</label>
                  <input 
                    type="date" 
                    id="load-in-date" 
                    name="loadInDate" 
                    required
                    class="input-full"
                  />
                </div>
                
                <div class="wishlist-submit-form__field">
                  <label for="load-in-time">Load-In Time *</label>
                  <input 
                    type="time" 
                    id="load-in-time" 
                    name="loadInTime" 
                    required
                    class="input-full"
                  />
                </div>
              </div>
              
              <div class="wishlist-submit-form__row">
                <div class="wishlist-submit-form__field">
                  <label for="load-out-date">Load-Out Date *</label>
                  <input 
                    type="date" 
                    id="load-out-date" 
                    name="loadOutDate" 
                    required
                    class="input-full"
                  />
                </div>
                
                <div class="wishlist-submit-form__field">
                  <label for="load-out-time">Load-Out Time *</label>
                  <input 
                    type="time" 
                    id="load-out-time" 
                    name="loadOutTime" 
                    required
                    class="input-full"
                  />
                </div>
              </div>
              
              <div class="wishlist-submit-form__field">
                <label for="venue-name">Venue Name *</label>
                <input 
                  type="text" 
                  id="venue-name" 
                  name="venueName" 
                  required
                  class="input-full"
                />
              </div>
              
              <div class="wishlist-submit-form__field">
                <label for="venue-address">Venue Address *</label>
                <textarea 
                  id="venue-address" 
                  name="venueAddress" 
                  required
                  rows="3"
                  class="input-full"
                ></textarea>
              </div>
              
              <div class="wishlist-submit-form__field">
                <label for="missing-items">Is there anything you need for your project you weren't able to find in our collection?</label>
                <textarea 
                  id="missing-items" 
                  name="missingItems" 
                  rows="3"
                  class="input-full"
                ></textarea>
              </div>
              
              <div class="wishlist-submit-form__field">
                <label for="project-notes">Project Notes</label>
                <textarea 
                  id="project-notes" 
                  name="projectNotes" 
                  rows="4"
                  class="input-full"
                ></textarea>
              </div>
            </div>
            
            <div class="wishlist-submit-form__actions">
              <button type="button" class="btn btn--soft" data-cancel-submit>Cancel</button>
              <button type="submit" class="btn btn--primary">Submit Wishlist</button>
            </div>
          </form>
        </div>
      `;

      document.body.appendChild(modal);
      modal.classList.add('is-active');

      // Pre-fill form with previous data if available
      if (previousData) {
        const loadInDateField = modal.querySelector('#load-in-date');
        const loadInTimeField = modal.querySelector('#load-in-time');
        const loadOutDateField = modal.querySelector('#load-out-date');
        const loadOutTimeField = modal.querySelector('#load-out-time');
        const venueNameField = modal.querySelector('#venue-name');
        const venueAddressField = modal.querySelector('#venue-address');
        
        if (previousData.loadInDate && loadInDateField) {
          // Format date for input (YYYY-MM-DD)
          const loadInDate = new Date(previousData.loadInDate);
          if (!isNaN(loadInDate.getTime())) {
            loadInDateField.value = loadInDate.toISOString().split('T')[0];
          }
        }
        
        if (previousData.loadInTime && loadInTimeField) {
          loadInTimeField.value = previousData.loadInTime;
        }
        
        if (previousData.loadOutDate && loadOutDateField) {
          // Format date for input (YYYY-MM-DD)
          const loadOutDate = new Date(previousData.loadOutDate);
          if (!isNaN(loadOutDate.getTime())) {
            loadOutDateField.value = loadOutDate.toISOString().split('T')[0];
          }
        }
        
        if (previousData.loadOutTime && loadOutTimeField) {
          loadOutTimeField.value = previousData.loadOutTime;
        }
        
        if (previousData.venueName && venueNameField) {
          venueNameField.value = previousData.venueName;
        }
        
        if (previousData.venueAddress && venueAddressField) {
          venueAddressField.value = previousData.venueAddress;
        }
      } else {
        // Pre-fill dates with defaults if no previous data
        const loadInDateField = modal.querySelector('#load-in-date');
        const loadOutDateField = modal.querySelector('#load-out-date');
        const loadInTimeField = modal.querySelector('#load-in-time');
        const loadOutTimeField = modal.querySelector('#load-out-time');
        
        if (loadInDateField && !loadInDateField.value) {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          loadInDateField.value = tomorrow.toISOString().split('T')[0];
        }
        
        if (loadOutDateField && !loadOutDateField.value) {
          const dayAfterTomorrow = new Date();
          dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
          loadOutDateField.value = dayAfterTomorrow.toISOString().split('T')[0];
        }
        
        if (loadInTimeField && !loadInTimeField.value) {
          loadInTimeField.value = '09:00';
        }
        
        if (loadOutTimeField && !loadOutTimeField.value) {
          loadOutTimeField.value = '17:00';
        }
      }

      // Initialize Google Places Autocomplete for venue address
      this.initGooglePlacesAutocomplete(modal);

      // Close handlers
      modal.querySelectorAll('[data-modal-close], [data-cancel-submit]').forEach(btn => {
        btn.addEventListener('click', () => {
          modal.remove();
          resolve(null);
        });
      });

      // Form submit handler
      const form = modal.querySelector('[data-wishlist-submit-form]');
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Get form data
        const formData = new FormData(form);
        const submissionData = {
          loadInDate: formData.get('loadInDate'),
          loadInTime: formData.get('loadInTime'),
          loadOutDate: formData.get('loadOutDate'),
          loadOutTime: formData.get('loadOutTime'),
          venueName: formData.get('venueName').trim(),
          venueAddress: formData.get('venueAddress').trim(),
          missingItems: formData.get('missingItems') ? formData.get('missingItems').trim() : null,
          projectNotes: formData.get('projectNotes') ? formData.get('projectNotes').trim() : null
        };
        
        // Validate dates
        const loadIn = new Date(`${submissionData.loadInDate}T${submissionData.loadInTime}`);
        const loadOut = new Date(`${submissionData.loadOutDate}T${submissionData.loadOutTime}`);
        
        if (loadOut <= loadIn) {
          if (window.WishlistDialogs) {
            await window.WishlistDialogs.alert('Load-out date/time must be after load-in date/time.', 'Invalid Dates');
          } else {
            alert('Load-out date/time must be after load-in date/time.');
          }
          return;
        }
        
        modal.remove();
        resolve(submissionData);
      });
    });
  }

  /**
   * Initialize address autocomplete for venue address field
   * Supports Algolia Places (free) or Google Places API
   */
  initGooglePlacesAutocomplete(modal) {
    const addressField = modal.querySelector('#venue-address');
    if (!addressField) {
      console.warn('Venue address field not found in modal');
      return;
    }

    const provider = window.wishlistConfig?.addressAutocompleteProvider || 'algolia';
    
    if (provider === 'algolia') {
      this.initAlgoliaPlaces(addressField);
    } else {
      this.initGooglePlaces(addressField);
    }
  }

  /**
   * Initialize Algolia Places autocomplete (free alternative)
   */
  initAlgoliaPlaces(addressField) {
    const appId = window.wishlistConfig?.algoliaPlacesAppId || '';
    const apiKey = window.wishlistConfig?.algoliaPlacesApiKey || '';
    
    if (!appId || !apiKey) {
      console.warn('Algolia Places credentials not configured. Autocomplete will not be available.');
      console.warn('Get free credentials at: https://www.algolia.com/users/sign_up');
      return;
    }

    // Check if Algolia Autocomplete is already loaded
    if (typeof autocomplete !== 'undefined' && typeof '@algolia/autocomplete-js' !== 'undefined') {
      this.setupAlgoliaAutocomplete(addressField, appId, apiKey);
      return;
    }

    // Check if script is already being loaded
    if (document.querySelector('script[data-algolia-places]')) {
      const checkInterval = setInterval(() => {
        if (typeof autocomplete !== 'undefined') {
          clearInterval(checkInterval);
          this.setupAlgoliaAutocomplete(addressField, appId, apiKey);
        }
      }, 100);
      
      setTimeout(() => clearInterval(checkInterval), 10000);
      return;
    }

    // Load Algolia Places script (using the correct CDN)
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/places.js@1.21.0';
    script.async = true;
    script.setAttribute('data-algolia-places', 'true');
    
    script.onload = () => {
      console.log('Algolia Places script loaded, initializing...');
      // Wait a bit for the library to fully initialize
      setTimeout(() => {
        this.setupAlgoliaAutocomplete(addressField, appId, apiKey);
      }, 200);
    };
    
    script.onerror = () => {
      console.error('Failed to load Algolia Places script. Trying alternative CDN...');
      // Try alternative CDN
      const altScript = document.createElement('script');
      altScript.src = 'https://unpkg.com/places.js@1.21.0';
      altScript.async = true;
      altScript.setAttribute('data-algolia-places', 'true');
      altScript.onload = () => {
        setTimeout(() => {
          this.setupAlgoliaAutocomplete(addressField, appId, apiKey);
        }, 200);
      };
      altScript.onerror = () => {
        console.error('Failed to load Algolia Places from both CDNs');
      };
      document.head.appendChild(altScript);
    };

    console.log('Loading Algolia Places script from CDN...');
    document.head.appendChild(script);
  }

  /**
   * Setup Algolia Places autocomplete
   */
  setupAlgoliaAutocomplete(addressField, appId, apiKey) {
    // Check for places.js library (it exposes a global 'places' function)
    const placesFn = typeof places !== 'undefined' ? places : (typeof window !== 'undefined' && window.places) ? window.places : null;
    
    if (!placesFn) {
      console.error('Algolia Places library not available. Available globals:', Object.keys(window).filter(k => k.toLowerCase().includes('place')));
      console.error('Trying to re-initialize in 1 second...');
      setTimeout(() => {
        this.setupAlgoliaAutocomplete(addressField, appId, apiKey);
      }, 1000);
      return;
    }

    if (addressField.dataset.autocompleteInitialized === 'true') {
      console.log('Autocomplete already initialized for this field');
      return;
    }

    try {
      console.log('Setting up Algolia Places Autocomplete with App ID:', appId.substring(0, 4) + '...');
      
      const placesAutocomplete = placesFn({
        appId: appId,
        apiKey: apiKey,
        container: addressField,
        type: 'address',
        templates: {
          value: function(suggestion) {
            return suggestion.value;
          }
        }
      });

      placesAutocomplete.on('change', (e) => {
        const suggestion = e.suggestion;
        console.log('Address selected:', suggestion);
        
        // Populate address field with formatted address
        if (suggestion && suggestion.value) {
          addressField.value = suggestion.value;
        }
        
        // Optionally populate venue name if empty
        const venueNameField = document.querySelector('#venue-name');
        if (venueNameField && !venueNameField.value && suggestion.name) {
          venueNameField.value = suggestion.name;
        }
      });

      placesAutocomplete.on('clear', () => {
        console.log('Address field cleared');
      });

      addressField.dataset.autocompleteInitialized = 'true';
      console.log('✅ Algolia Places Autocomplete initialized successfully');
    } catch (error) {
      console.error('❌ Error setting up Algolia Places Autocomplete:', error);
      console.error('Error details:', error.message);
      if (error.stack) {
        console.error('Stack:', error.stack);
      }
    }
  }

  /**
   * Initialize Google Places API autocomplete (original implementation)
   */
  initGooglePlaces(addressField) {
    const apiKey = window.wishlistConfig?.googlePlacesApiKey || '';
    if (!apiKey) {
      console.warn('Google Places API key not configured. Autocomplete will not be available.');
      return;
    }

    // Check if Google Places API is already loaded
    if (typeof google !== 'undefined' && google.maps && google.maps.places) {
      console.log('Google Places API already loaded, setting up autocomplete');
      this.setupAutocomplete(addressField);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[data-google-places-api]');
    if (existingScript) {
      console.log('Google Places API script already loading, waiting...');
      const checkInterval = setInterval(() => {
        if (typeof google !== 'undefined' && google.maps && google.maps.places) {
          clearInterval(checkInterval);
          console.log('Google Places API loaded, setting up autocomplete');
          this.setupAutocomplete(addressField);
        }
      }, 100);
      
      setTimeout(() => {
        clearInterval(checkInterval);
        if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
          console.error('Google Places API failed to load within timeout');
        }
      }, 10000);
      return;
    }

    // Create unique callback name
    const callbackName = 'initGooglePlacesAutocomplete_' + Date.now();
    
    window[callbackName] = () => {
      console.log('Google Places API callback fired');
      if (typeof google !== 'undefined' && google.maps && google.maps.places) {
        setTimeout(() => {
          this.setupAutocomplete(addressField);
        }, 100);
      } else {
        console.error('Google Places API not available in callback');
      }
      delete window[callbackName];
    };

    // Load Google Places API
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    script.setAttribute('data-google-places-api', 'true');
    
    script.onerror = () => {
      console.error('Failed to load Google Places API script');
      delete window[callbackName];
    };

    console.log('Loading Google Places API script...');
    document.head.appendChild(script);
  }

  /**
   * Setup autocomplete on address field
   */
  setupAutocomplete(addressField) {
    if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
      console.error('Google Places API not available in setupAutocomplete');
      return;
    }

    // Check if autocomplete is already set up on this field
    if (addressField.dataset.autocompleteInitialized === 'true') {
      console.log('Autocomplete already initialized on this field');
      return;
    }

    try {
      console.log('Setting up Google Places Autocomplete');
      const autocomplete = new google.maps.places.Autocomplete(addressField, {
        types: ['establishment', 'geocode'], // Include both places and addresses
        fields: ['formatted_address', 'name', 'geometry', 'address_components']
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        console.log('Place selected:', place);
        
        if (place.formatted_address) {
          // Populate the address field with the formatted address
          addressField.value = place.formatted_address;
          
          // Optionally populate venue name if it's empty and we have a place name
          const venueNameField = document.querySelector('#venue-name');
          if (venueNameField && !venueNameField.value && place.name) {
            venueNameField.value = place.name;
          }
        }
      });

      // Mark as initialized
      addressField.dataset.autocompleteInitialized = 'true';
      console.log('Google Places Autocomplete initialized successfully');
    } catch (error) {
      console.error('Error setting up Google Places Autocomplete:', error);
    }
  }

  async showCreateModal() {
    let name;
    if (window.WishlistDialogs) {
      name = await window.WishlistDialogs.prompt('Enter wishlist name:', '', 'Create Wishlist');
    } else {
      name = prompt('Enter wishlist name:');
    }
    
    if (!name) {
      return;
    }

    await this.createWishlist(name);
  }

  async createWishlist(name) {
    if (!this.api) {
      this.showError('WishlistAPI not available. Please refresh the page.');
      return;
    }

    try {
      await this.api.createWishlist({ name });
      await this.loadWishlists();
    } catch (error) {
      console.error('Failed to create wishlist:', error);
      if (window.WishlistDialogs) {
        await window.WishlistDialogs.alert('Failed to create wishlist. Please try again.', 'Error');
      } else {
        alert('Failed to create wishlist. Please try again.');
      }
    }
  }

  showError(message) {
    const content = document.querySelector('[data-wishlist-content]');
    if (content) {
      content.innerHTML = `
        <div style="text-align: center; padding: 40px;">
          <h2>Error</h2>
          <p>${message}</p>
        </div>
      `;
    }
  }

  /**
   * Update create button visibility based on authentication state
   */
  updateCreateButtonVisibility(isAuthenticated) {
    const createBtn = document.querySelector('[data-create-wishlist]');
    if (createBtn) {
      createBtn.style.display = isAuthenticated ? '' : 'none';
    }
  }

  showAuthRequired() {
    // Hide create button and page header when logged out
    this.updateCreateButtonVisibility(false);
    
    // Add class to page for reduced spacing and header hiding
    const page = document.querySelector('.wishlist-page');
    if (page) {
      page.classList.add('is-logged-out');
    }
    
    // Also explicitly hide header
    const header = document.querySelector('[data-wishlist-header]');
    if (header) {
      header.style.display = 'none';
    }

    const content = document.querySelector('[data-wishlist-content]');
    if (content) {
      content.innerHTML = `
        <div class="wishlist-page__auth-required">
          <div class="wishlist-page__auth-content">
            <h1 class="wishlist-page__auth-title standard__heading heading-size-8">Your Wishlists</h1>
            <p class="wishlist-page__auth-subhead standard__kicker">Sign in to continue</p>
            <p class="wishlist-page__auth-body body-size-4">
              Create personalized wishlists for each project. Save pieces, check availability, and submit selections for pricing and proposals—then return anytime for updates as your work evolves.
            </p>
            <div class="wishlist-page__auth-actions standard__cta__wrapper">
              <button class="btn btn--primary" data-show-auth-prompt type="button">
                Sign In
              </button>
              <button class="btn btn--primary" data-show-auth-register type="button">
                Create an Account
              </button>
            </div>
            <div class="wishlist-page__auth-secondary">
              <a href="/pages/reserve" class="wishlist-page__auth-link">
                Apply for Mayker Reserve
              </a>
            </div>
          </div>
        </div>
      `;
      
      // Add click handlers
      const signInButton = content.querySelector('[data-show-auth-prompt]');
      const registerButton = content.querySelector('[data-show-auth-register]');
      
      if (signInButton && this.auth && this.auth.showAuthPrompt) {
        signInButton.addEventListener('click', () => {
          this.auth.showAuthPrompt();
          
          // After successful auth, reload wishlists
          const reloadOnAuth = () => {
            this.updateCreateButtonVisibility(true);
            const header = document.querySelector('[data-wishlist-header]');
            if (header) {
              header.style.display = '';
            }
            const page = document.querySelector('.wishlist-page');
            if (page) {
              page.classList.remove('is-logged-out');
            }
            this.loadWishlists();
            document.removeEventListener('wishlist:auth:success', reloadOnAuth);
          };
          document.addEventListener('wishlist:auth:success', reloadOnAuth, { once: true });
        });
      }
      
      if (registerButton && this.auth && this.auth.showAuthPrompt) {
        registerButton.addEventListener('click', () => {
          // Show auth modal and switch to register tab
          this.auth.showAuthPrompt();
          
          // Wait for modal to be created, then switch to register tab
          setTimeout(() => {
            const modal = document.getElementById('wishlist-auth-modal');
            if (modal) {
              const registerTab = modal.querySelector('[data-tab="register"]');
              if (registerTab) {
                registerTab.click();
              }
            }
          }, 100);
          
          // After successful auth, reload wishlists
          const reloadOnAuth = () => {
            this.updateCreateButtonVisibility(true);
            const header = document.querySelector('[data-wishlist-header]');
            if (header) {
              header.style.display = '';
            }
            const page = document.querySelector('.wishlist-page');
            if (page) {
              page.classList.remove('is-logged-out');
            }
            this.loadWishlists();
            document.removeEventListener('wishlist:auth:success', reloadOnAuth);
          };
          document.addEventListener('wishlist:auth:success', reloadOnAuth, { once: true });
        });
      }
    }
  }
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('.wishlist-page')) {
    window.WishlistPage = new WishlistPage();
  }
});
